import { supabase } from '../supabase';

// Tipos para las respuestas de autenticación
export interface AuthResponse {
  success: boolean;
  message: string;
  data?: {
    user: any;
    session: any;
  };
}

// Interfaz para el registro de usuarios
export interface SignUpData {
  email: string;
  password: string; // Ya no es opcional, debe ser proporcionada por el usuario
  name?: string;
  phone?: string;
  company?: string; // Añadir campo company
  user_metadata?: Record<string, any>; // Campo para metadatos personalizados
}

// Interfaz para el inicio de sesión
export interface SignInData {
  email: string;
  password: string;
}

// Clase para manejar la autenticación
class AuthService {
  // Método para generar una contraseña aleatoria segura - lo mantenemos por si es necesario en el futuro
  private generateSecurePassword(length = 10): string {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+';
    let password = '';

    // Asegurar que tenga al menos una mayúscula, una minúscula, un número y un carácter especial
    password += 'A'; // Una mayúscula
    password += 'a'; // Una minúscula
    password += '1'; // Un número
    password += '!'; // Un carácter especial

    // Completar el resto de la contraseña
    for (let i = password.length; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }

    // Mezclar los caracteres para que no sean predecibles
    return password.split('').sort(() => Math.random() - 0.5).join('');
  }

  // Método alternativo: usar directamente Supabase Client cuando la Edge Function falla
  async signUpWithSupabaseClient(data: SignUpData): Promise<AuthResponse> {
    try {
      console.log('🔍 Ejecutando signUpWithSupabaseClient con datos:', { email: data.email, name: data.name }); // No mostrar password

      const userPassword = data.password;

      // Intentar determinar si estamos en desarrollo o producción
      const isDevelopment = window.location.hostname === 'localhost' ||
                           window.location.hostname === '127.0.0.1';

      console.log('🌐 Entorno de ejecución:', isDevelopment ? 'Desarrollo' : 'Producción');
      console.log('🔄 Intentando registro con supabase-js directamente');

      // Preparar los metadatos para la plantilla de correo y el perfil
      const userMetadataForEmail = {
        registered_name: data.name || '',
        registered_email: data.email,
        registered_password: userPassword // Incluir contraseña para el correo
      };

      // Metadatos adicionales que no queremos en el correo pero sí en el perfil
      const profileMetadata = {
        phone: data.phone || '',
        company: data.company || ''
      };

      // Combinar metadatos para options.data (los que necesita el email)
      const metadataForOptions = { ...userMetadataForEmail };

      console.log('📋 Metadatos del usuario a pasar en options.data:', metadataForOptions);

      const { data: authData, error } = await supabase.auth.signUp({
        email: data.email,
        password: userPassword,
        options: {
          data: metadataForOptions, // Pasar los metadatos necesarios para el email
          emailRedirectTo: 'https://innotecfood.eu'
        }
      });

      if (error) {
        console.error('❌ Error con supabase-js auth.signUp:', error.message, error);
        // Intentar dar más detalles si es un error de plantilla
        if (error.message.includes('template')) {
          return {
            success: false,
            message: `Error al procesar la plantilla de correo: ${error.message}`,
          };
        }
        return {
          success: false,
          message: error.message || 'Error al registrarse',
        };
      }

      console.log('✅ Registro exitoso con supabase-js:', authData);

      // Verificar si el correo realmente se envió
      if (authData?.user) {
        console.log('📧 Estado del correo:', {
          confirmationSent: authData.user.confirmation_sent_at,
          emailConfirmed: authData.user.email_confirmed_at,
          userID: authData.user.id,
          userMetadataFromAuth: authData.user.user_metadata // Metadatos devueltos por signUp
        });
      }

      // Crear o actualizar el perfil del usuario en la tabla 'profiles' con todos los datos
      if (authData?.user?.id) {
        try {
          console.log('👤 Creando/Actualizando perfil para el usuario en la base de datos');
          const profileDataToInsert = {
            id: authData.user.id,
            email: data.email,
            name: data.name,
            ...profileMetadata, // Añadir teléfono y compañía
            // No guardamos la contraseña aquí si no es necesario
            email_confirmed: false // Marcar como no confirmado inicialmente
          };

          // Usamos upsert para manejar casos donde el trigger ya creó el perfil
          const { error: profileError } = await supabase
            .from("profiles")
            .upsert(profileDataToInsert, { onConflict: 'id' });

          if (profileError) {
            console.warn('⚠️ Error al crear/actualizar perfil:', profileError);
          } else {
            console.log('✅ Perfil creado/actualizado con éxito');
          }
        } catch (profileError) {
          console.error('❌ Excepción al crear/actualizar perfil:', profileError);
        }
      }

      // Guardar datos de la sesión si es exitoso
      if (authData?.session?.access_token) {
        localStorage.setItem('supabase.auth.token', authData.session.access_token || '');
        localStorage.setItem('supabase.auth.user', JSON.stringify(authData.user || {}));
        console.log('🔐 Datos de sesión guardados en localStorage');
      } else {
        console.log('ℹ️ No hay datos de sesión disponibles (normal si requiere confirmación de email)');
      }

      return {
        success: true,
        message: 'Usuario registrado correctamente. Por favor, confirma tu cuenta a través del correo que recibirás.',
        data: {
          user: authData.user,
          session: authData.session
        }
      };
    } catch (error: any) {
      console.error('❌ Error general en el registro directo con Supabase Client:', error);
      return {
        success: false,
        message: error.message || 'Error al registrarse',
      };
    }
  }

  // Método alternativo para iniciar sesión directamente con Supabase Client
  async signInWithSupabaseClient(data: SignInData): Promise<AuthResponse> {
    try {
      console.log('Ejecutando signInWithSupabaseClient con email:', data.email);

      // Intentar determinar si estamos en desarrollo o producción
      const isDevelopment = window.location.hostname === 'localhost' ||
                           window.location.hostname === '127.0.0.1';

      console.log('Entorno de ejecución:', isDevelopment ? 'Desarrollo' : 'Producción');
      console.log('Intentando inicio de sesión con supabase-js directamente');

      // Verificar si son usuarios admin específicos (modo legacy)
      if (data.email === 'admin' && data.password === 'admin123') {
        console.log('Iniciando sesión con usuario admin legacy');
        // Redirigir al login con usuario real de administrador
        return {
          success: false,
          message: 'El método de acceso admin/admin123 está obsoleto. Por favor, utiliza las credenciales reales del administrador.',
        };
      }

      // Usuarios regulares: usar el cliente de supabase directamente
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password
      });

      if (error) {
        console.error('Error con supabase-js auth.signInWithPassword:', error.message, error);
        return {
          success: false,
          message: error.message || 'Error al iniciar sesión',
        };
      }

      console.log('Inicio de sesión exitoso con supabase-js:', authData);

      // Guardar datos de la sesión si es exitoso
      if (authData?.session?.access_token) {
        localStorage.setItem('supabase.auth.token', authData.session.access_token || '');
        localStorage.setItem('supabase.auth.user', JSON.stringify(authData.user || {}));
      }

      // Verificar si el usuario es administrador consultando su perfil
      if (authData?.user?.id) {
        try {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('is_admin, role')
            .eq('id', authData.user.id)
            .single();

          if (!profileError && profileData) {
            console.log('Datos de perfil del usuario:', profileData);

            // Si es administrador, añadir esta información a los metadatos del usuario
            if (profileData.is_admin === true || profileData.role === 'admin') {
              console.log('Usuario identificado como administrador');

              // Actualizar los metadatos guardados para incluir role=admin
              const userWithAdminRole = {
                ...authData.user,
                user_metadata: {
                  ...authData.user.user_metadata,
                  role: 'admin',
                  is_admin: true
                }
              };

              // Guardar la versión actualizada en localStorage
              localStorage.setItem('supabase.auth.user', JSON.stringify(userWithAdminRole));

              return {
                success: true,
                message: 'Sesión de administrador iniciada correctamente',
                data: {
                  user: userWithAdminRole,
                  session: authData.session
                }
              };
            }
          }
        } catch (profileError) {
          console.error('Error al verificar si el usuario es administrador:', profileError);
          // Continuar con el inicio de sesión normal aunque falle esta verificación
        }
      }

      return {
        success: true,
        message: 'Sesión iniciada correctamente',
        data: {
          user: authData.user,
          session: authData.session
        }
      };
    } catch (error: any) {
      console.error('Error en el inicio de sesión directo con Supabase Client:', error);
      return {
        success: false,
        message: error.message || 'Error al iniciar sesión',
      };
    }
  }

  // URL base para las funciones de edge de autenticación
  private baseUrl = 'https://jlufgkgssnxumguvknhc.supabase.co/functions/v1/auth';

  // Función auxiliar para verificar el estado de las edge functions
  private async checkEdgeFunctionStatus(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      return response.ok;
    } catch (error) {
      console.error('Error al verificar el estado de las Edge Functions:', error);
      return false;
    }
  }

  // Verificar si el usuario está autenticado
  isAuthenticated(): boolean {
    const token = localStorage.getItem('supabase.auth.token');
    return !!token;
  }

  // Obtener el token de autenticación
  getToken(): string {
    return localStorage.getItem('supabase.auth.token') || '';
  }

  // Obtener los datos del usuario actual
  getCurrentUser(): any {
    const userStr = localStorage.getItem('supabase.auth.user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Método principal para registro - intenta Edge Functions primero, luego directamente
  async signUp(data: SignUpData): Promise<AuthResponse> {
    try {
      console.log('Intentando registrar usuario con datos:', { email: data.email, name: data.name });

      // Usar directamente el cliente de Supabase para registrarse
      return this.signUpWithSupabaseClient(data);

    } catch (error: any) {
      console.error('Error general en signUp:', error);
      return {
        success: false,
        message: error.message || 'Error al registrarse',
      };
    }
  }

  // Método principal para inicio de sesión - intenta Edge Functions primero, luego directamente
  async signIn(data: SignInData): Promise<AuthResponse> {
    try {
      console.log('Intentando iniciar sesión con email:', data.email);

      // Usar directamente el cliente de Supabase para iniciar sesión
      return this.signInWithSupabaseClient(data);

    } catch (error: any) {
      console.error('Error general en signIn:', error);
      return {
        success: false,
        message: error.message || 'Error al iniciar sesión',
      };
    }
  }

  // Método para cambiar la contraseña del usuario
  async changePassword(currentPassword: string, newPassword: string): Promise<AuthResponse> {
    try {
      console.log('Intentando cambiar contraseña');

      // Primero verificamos que la sesión esté activa
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !sessionData.session) {
        console.error('Error al verificar la sesión:', sessionError);
        return {
          success: false,
          message: 'No hay una sesión activa. Por favor, inicia sesión nuevamente.'
        };
      }

      // Actualizar la contraseña
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        console.error('Error al cambiar contraseña:', error);
        return {
          success: false,
          message: error.message || 'Error al cambiar la contraseña'
        };
      }

      console.log('Contraseña cambiada correctamente');
      return {
        success: true,
        message: 'Contraseña cambiada correctamente'
      };
    } catch (error: any) {
      console.error('Error general en changePassword:', error);
      return {
        success: false,
        message: error.message || 'Error al cambiar la contraseña'
      };
    }
  }

  // Método para reenviar email de confirmación
  async resendConfirmationEmail(email: string): Promise<AuthResponse> {
    try {
      console.log('🔄 Intentando reenviar correo de confirmación a:', email);

      const { data, error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: 'https://innotecfood.eu'
        }
      });

      if (error) {
        console.error('❌ Error al reenviar correo de confirmación:', error);
        console.error('Detalles del error:', error.message, error.status);
        return {
          success: false,
          message: error.message || 'Error al reenviar el correo de confirmación'
        };
      }

      console.log('✅ Solicitud de reenvío procesada:', data);
      return {
        success: true,
        message: 'Correo de confirmación enviado correctamente. Revisa tu bandeja de entrada.'
      };
    } catch (error: any) {
      console.error('❌ Error general en resendConfirmationEmail:', error);
      return {
        success: false,
        message: error.message || 'Error al reenviar el correo de confirmación'
      };
    }
  }

  // Método para probar la conexión con Supabase y verificar la configuración
  async testSupabaseConnection(): Promise<{success: boolean, details: any}> {
    try {
      console.log('🧪 Probando conexión con Supabase...');

      // Verificar la URL y la clave de Supabase
      // @ts-ignore - Vite expone import.meta.env pero TypeScript no lo reconoce por defecto
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
      // @ts-ignore
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

      console.log('🔑 Credenciales Supabase disponibles:', {
        url: supabaseUrl ? '✅ Configurada' : '❌ No configurada',
        key: supabaseKey ? '✅ Configurada' : '❌ No configurada',
        keyLength: supabaseKey ? supabaseKey.length : 0
      });

      // Probar una consulta simple para verificar la conexión
      const { data, error } = await supabase.from('profiles').select('count', { count: 'exact', head: true });

      if (error) {
        console.error('❌ Error al conectar con Supabase:', error);
        return {
          success: false,
          details: {
            error: error.message,
            code: error.code,
            hint: error.hint,
            details: error.details
          }
        };
      }

      // Probar la configuración de Auth
      const authSettings = await supabase.auth.getSession();

      console.log('✅ Conexión exitosa con Supabase. Auth settings:', {
        session: authSettings.data.session ? 'Disponible' : 'No disponible',
        error: authSettings.error ? authSettings.error.message : 'Ninguno'
      });

      return {
        success: true,
        details: {
          connection: 'OK',
          auth: {
            sessionAvailable: !!authSettings.data.session,
            error: authSettings.error
          }
        }
      };
    } catch (error: any) {
      console.error('❌ Error general al probar conexión:', error);
      return {
        success: false,
        details: {
          error: error.message || 'Error desconocido',
          stack: error.stack
        }
      };
    }
  }
}

// Exportar una instancia del servicio
export const authService = new AuthService();
